import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Participants } from 'app/main/signing-pages/signing-sidebar/participants/participants.dto';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { IAuditInterface } from '../../../global/audit.interface';
import { ActivityLogs } from '../../signing-pages/signing-sidebar/activity-logs/activity-logs-dto';
import { GetRelatedContract, IMasterContract } from '../contract-control/contract-control.dto';
import { ManagerDTO } from '../contract-control/store/dto/manager-list.dto';
import { Organization, Recipient, TemplateForm } from '../edit-template/edit-template.dto';
import { IMetadataItemDto } from '../settings/documentfields/metadata-item.dto';
import { IDocumentEntitySettings } from './document-right-sidebar/document-details/document-details.dto';
import { IContractData } from './dto/contract-data.dto';
import {
    DocumentInternalReminder,
    IDocumentDetailRootDTO,
    Template as IndexDataTemplate, TemplateDetails as IndexDataTemplateDetails,
    Tag
} from './dto/document-details.dto';
import { IDocumentTypesDto, IGetDocumentByIdAndNumber } from './dto/document-listing.dto';
import { DocumentStatusesEnum } from './dto/document-statuses.enum';
import { IUpdateFieldsData } from './store/document-listing.facade';


@Injectable({
    providedIn: 'root'
})

export class DocumentsService {
    constructor(private http: HttpClient) {

    }

    /**
     * Create document
     * @param document 
     */
    createDocument(document) {
        return this.http.post<IDocumentDetailRootDTO>(`documents`, document);
    }

    /**
     * Get template behalf on ${id}
     * @param id 
     * @returns 
     */
    getDocument(id: number): Observable<IDocumentDetailRootDTO> {
        return this.http.get<IDocumentDetailRootDTO>(`documents/${id}`);
    }


    /**
     * Get Document Index Data
     * @param id 
     * @returns Response
     */
    getDocumentIndexData(id: number): Observable<IDocumentDetailRootDTO> {
        return this.http.get<IDocumentDetailRootDTO>(`documents/${id}/index-data`);
    }


    /**
     * Update document
     * @param form 
     * @returns 
     */
    updateDocument(form: any) { // eslint-disable-line
        return this.http.put(`documents/${form.template?.id}`, form);//TODO: add type when templates tupe will be defined
    }

    /**
     * save as draft
     * @param form 
     * @returns 
     */
    saveAsDraft(form: any) {// eslint-disable-line
        return this.http.put(`documents/${form.template?.id}/save-draft`, form);//TODO: add type when templates tupe will be defined
    }

    /**
     * Update document status
     * @returns
     * @param documentId
     */
    documentRevoke(documentId: number) {
        return this.http.post(`documents/${documentId}/revoke-document`, {});
    }



    /**
     * send document to the recipient
     * @param form
     */
    sendDocument(form: any) {// eslint-disable-line
        return this.http.post(`documents/${form.template.id}/send-document`, form);//TODO: add type when templates tupe will be defined
    }

    /**
     * Add role in template
     * @param formData 
     */
    addTemplateRole(formData) {
        return this.http.post(`documents/${formData.document_id}/document-role`, formData);
    }


    /**
     * Link contract control contract with document
     * @param documentId 
     * @param formData 
     * @returns 
     */
    linkContract(formData: { id: number, updatedResult: IContractData }) {
        //TODO: endpoint seems to be not described on APIDog
        return this.http.post<ILinkContractControl>(`documents/${formData.id}/contract-control`, formData.updatedResult);
    }



    /**
     * Update due date
     * @param formData
     * @returns 
     */
    updateDueDate(formData: IUpdateDueDateParams): Observable<IUpdateDueDateResponse> {
        return this.http.post<IUpdateDueDateResponse>(`documents/${formData?.documentId}/change-due-date`, formData);
    }



    /**
     * Update document visibility
     * @param formData 
     * @returns 
     */
    updateDocumentVisibility(formData: { documentId: number, visibility: string }) {
        return this.http.post(`documents/${formData?.documentId}/change-visibility`, { visibility: formData?.visibility });
    }


    /**
     * Update document status
     * @returns
     * @param documentId
     * @param status
     */
    updateDocumentStatus(documentId: number, status: string) {
        return this.http.post(`documents/${documentId}/change-status`, { status: status })
    }


    /**
     * Get Shareable Document Content
     * @param documentId 
     * @param documentNumber 
     * @returns 
     */
    getShareableDocument(documentId: string, documentNumber: string) {
        return this.http.get<IGetDocumentByIdAndNumber>(`documents/${documentId}/${documentNumber}`);
    }

    /**
     * Add tags in document
     * @param documentId 
     * @param tag
     * @returns 
     */
    addTags(documentId: number, tag: Tag) {
        return this.http.post(`documents/${documentId}/add-tags`, { "tags": [tag] });
    }


    /**
     * Remove Tag
     * @param documentId 
     * @param tag 
     * @returns 
     */
    removeTag(documentId: number, tag: Tag) {
        return this.http.post(`documents/${documentId}/remove-tag`, tag);
    }



    /**
     * Update Document Type
     * @param documentId 
     * @param documentTypeId 
     * @returns 
     */
    updateDocumentType(documentId: number, documentTypeId: number) {
        return this.http.post(`documents/${documentId}/change-document-type`, { document_type_id: documentTypeId });
    }

    /**
     * Update Document Type
     * @param documentId
     * @param categoryId
     * @returns
     */
    updateCategory(documentId: number, categoryIds: number[]): Observable<IDocumentDetailRootDTO> {
        return this.http.post<IDocumentDetailRootDTO>(`documents/${documentId}/change-category`, { category_ids: categoryIds });
    }


    /**
     * Fetch Document Participants
     * @param documentId 
     * @returns 
     */
    getDocumentsParticipants(documentId: number) {
        return this.http.get<Participants>(`documents/${documentId}/participants`);
    }



    /**
     * Fetch Document Activity Chat
     * @param documentId 
     * @returns 
     */
    fetchDocumentActivity(documentId: number) {
        return this.http.get<ActivityLogs[]>(`documents/${documentId}/activity-chat`);
    }



    /**
     * Save Document Activity
     * @param documentId 
     * @param formData
     * @returns 
     */
    sendDocumentChat(documentId: number, formData: FormData) {
        return this.http.post<ActivityLogs>(`documents/${documentId}/activity-chat`, formData);
    }


    /**
     * Update Document Activity Status
     * @param roleId 
     * @param activityIds 
     * @returns 
     */
    updateDocumentActivityStatus(roleId: number, activityIds: number[]) {
        return this.http.post(`documents/${roleId}/activity-chat-status`, { activity_ids: activityIds });
    }


    /**
     * Download document PDF
     * @param documentId 
     * @returns 
     */
    downloadPDF(documentId: number) {
        return this.http.get(`documents/${documentId}/download/pdf`, { responseType: 'blob' });
    }


    /**
     * Update Document Name
     * @param documentId 
     * @param name
     * @returns 
     */
    updateDocumentName(documentId: number, name: string) {
        return this.http.post(`documents/${documentId}/update-name`, { name: name });
    }

    /**
     * Update Document Name
     * @param documentId
     * @param formData
     * @returns
     */
    setReminder(documentId: number, formData: {
        id?: number,
        reminder_datetime: string,
        message: string
        user_ids: number[]
    }
    ): Observable<DocumentInternalReminder> {
        formData.reminder_datetime = moment(formData.reminder_datetime).format('YYYY-MM-DD HH:mm');
        return this.http.post<DocumentInternalReminder>(`documents/${documentId}/set-internal-reminder`, formData);
    }

    /**
     * Delete internal reminder
     * @param documentId
     * @param reminderId
     * @returns 
     */
    deleteInternalReminder(documentId: number, reminderId: number) {
        return this.http.delete(`documents/${documentId}/internal-reminder/${reminderId}`);
    }

    /**
     * create a document thumbnail
     * @param documentId 
     * @returns 
     */
    createThumbnai(documentId: number) {
        return this.http.post(`documents/${documentId}/thumbnail`, {});
    }

    /**
     * Update the recipient information
     * @param document_id 
     * @param roledata 
     * @returns response
     */
    updateParticipant(document_id: number, roledata) {
        return this.http.post(`documents/${document_id}/upgrade-recipient-role`, roledata);
    }

    /**
     * Resend document
     * @param document_id
     * @param data
     * @returns
     */
    resendDocument(document_id: number, data: IDocumentDetailRootDTO) {
        return this.http.post<TemplateForm>(`documents/${document_id}/resend-document`, data);
    }



    /**
     * Resend email to recipient when status getting failed on email
     * @param formData 
     * @returns 
     */
    resendEmail(documentId: number, formData: IRequestResendEmailBody) {
        return this.http.post(`documents/${documentId}/resend-email`, formData)
    }


    /**
     * Updates the value of a preview column by sending the provided form data to the server.
     *
     * @param formData - The data containing the updated value for the preview column.
     * @returns An `Observable` that emits the server's response containing the updated preview column details.
     */
    updatePreviewColumnValue(formData: IUpdatePreviewColumnFormData): Observable<IUpdatePreviewColumnResponse> {
        return this.http.post<IUpdatePreviewColumnResponse>('fieldsdatas/update-value', formData);
    }


    /**
     * Resets the value of the document preview column using the provided form data.
     *
     * @param formData - The data required to update the preview column.
     * @returns An observable that emits the response containing the updated preview column information.
     */
    resetDocumentPreviewColumnValue(formData: IUpdatePreviewColumnFormData): Observable<IUpdatePreviewColumnResponse> {
        return this.http.post<IUpdatePreviewColumnResponse>('fieldsdatas/reset', formData);
    }



    updateFieldsData(formData: IUpdateFieldsData) {
        return this.http.post('fieldsdatas/update-value', formData);
    }

    checkSpecialActions(documentId: number): Observable<ISpecialActionResponse> {
        return this.http.get<ISpecialActionResponse>(`documents/${documentId}/has-special-actions`);
    }

    resendToMetodika(documentId: number): Observable<any> {
        return this.http.post(`documents/${documentId}/resend-to-metodika`, {});
    }




    updateDocumentEntityData(documentId: number, settings: IDocumentEntitySettings): Observable<any> {
        return this.http.post(`documents/${documentId}/entity`, settings);
    }
}

export interface ISpecialActionResponse {
    additional_action: string | null;
}


export interface IUpdatePreviewColumnResponse {
    message: string
}

export interface IUpdatePreviewColumnFormData {
    dataable_type: string;
    dataable_id: number;
    selected_field_type_id: string;
    value?: string | number;
    uuid?: string
    meta_data?: IMetadataItemDto
}



export interface IRequestResendEmailBody {
    recipient_role_id: number,
    email: string
}


export interface IUpdateParticipantBody {
    id: number,
    role_name: string,
    role_color: string,
    sender_as_role: 0 | 1,
    recipient_id?: number,
    organization_id?: number,
    invitation_type: string,
    invitation_order: number,
    authentication_type: string, //TODO: introduce enum
    authentication_required: boolean,
    confirmation_type: string, //TODO introduce enum
    signature_confirmation?: string,
    personfields: { //TODO confirm if it's not a duplicate
        id: number,
        is_enable: 0 | 1,
        name: string,
        value: string,
        required: 1,
        validation: 0 | 1,
        is_default: 0 | 1
    },
    orgfields: {
        is_enable: 0 | 1,
        id: number,
        name: string,
        value: string,
        required: 0 | 1,
        validation: 0 | 1,
        is_default: 0 | 1
    },
    recipient: Partial<Recipient>,
    organization: Partial<Organization>
}



export interface IGetDocumentIndexData extends IAuditInterface {
    master_contract?: IMasterContract;
    contract_type: number;
    contract_control_multi_sections: string[];
    document_internal_reminder?: null | string;
    tags: { [key: string]: Tag }[];
    template: IndexDataTemplate;
    template_details: IndexDataTemplateDetails;
    template_fields_datas: TemplateFieldsDatas;
    template_roles: TemplateRole[];
    sub_contracts: string[];
    is_active: 0 | 1;
    document_type: DocumentType;
    managers: ManagerDTO[];
    related_contract: GetRelatedContract
}

export interface TemplateDetails {
    due_date?: null | string;
    id: number;
    language: string;
    pdf_path?: null | string;
    status: IndexDataDocumentStatus;
    status_updated_at?: null | string;
    template_id?: number | null;
    visible_name?: null | string;

}

export enum IndexDataDocumentStatus {
    Approved = "approved",
    Delivered = "delivered",
    Draft = "draft",
    Expired = "expired",
    Failed = "failed",
    Revoked = "revoked",
    Sent = "sent",
    Signed = "signed",
    Uploaded = "uploaded",
    Viewed = "viewed",
}

export interface TemplateFieldsDatas {
    created_at: string;
    data: { [key: string]: unknown }; //TODO: APIDOG does not provide information
    dataable_id: number;
    dataable_type: string;
    id: number;
    updated_at: string;
    user_id: number;
    workspace_id: number;
}

export interface TemplateRole {
    authentication_required: number;
    authentication_type: string;
    confirmation_type: string;
    created_at: string;
    id: number;
    invitation_order: number;
    invitation_type: string;
    is_default: number;
    organization: null;
    organization_id?: number | null;
    recipient: null;
    recipient_id?: number | null;
    role_color: string;
    role_name: string;
    roleable_id: number;
    roleable_type: string;
    sender_as_role: number;
    signature_confirmation: null;
    updated_at: string;
    user_id: number;
    workspace_id: number;
}


export interface Template {
    content: string;
    created_at: string;
    file?: null | string;
    id: number;
    name: string;
    status: DocumentStatusesEnum;
    status_updated_at?: null | string;
    template_id?: number | null;
    thumbnail?: null | string;
    updated_at: string;
    user_id: number;
}

export interface ILinkContractControl {
    id: number;
}

export interface IUpdateDueDateParams {
    documentId: number;
    due_date: string;
}

export interface IUpdateDueDateResponse {
    message: string;
    document_status: string
}
